import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';

export type TRole = 'user' | 'manager' | 'admin' | 'superadmin';
export type TTier = 'Integrated' | 'Enterprise Unlimited' | 'Enterprise';

export type TCredentials = {
  email: string;
  password: string;
  source: 'powercall' | 'dash';
};

export type CRMs = {
  sp: string;
  result: CRM[];
};

export type Queue = {
  queueid?: number;
  queuename?: string;
  subscribed?: 0 | 1;
  userid?: number;
};

export type CRM = {
  accountid: string | number;
  crmID: number;
  crmName: string;
  isActive: 0 | 1;
  location: string;
  logo: string;
  pass: string;
  token: string;
  user: string;
  userAgent: string;
  errordate: string;
  bcrmID?: number | string | null;
  businessid?: number | string | null;
  buser?: number | string | null;
  blocation?: number | string | null;
  bisActive?: number | string | null;
  ucrmID?: number;
  userid?: number;
};

export type TDevice = {
  sipUserId: number;
  userId: number;
  sipAddress: string;
  deviceType: 'webrtc' | 'mobileapp' | 'external' | 'desk';
  DID: string | null;
  createdate: string | null;
  updatedate: string | null;
  deviceid: string;
  zohoUserId: string | null;
  deviceName: string | null;
  phoneType: string;
  ext: string | null;
  cred: string;
  endpointid: string;
  didCountryCode: string | null;
  newPhoneType: string;
};

export type TDefaultDevice = {
  defaultC2C: string;
  deviceType: string;
  deviceName: string | null;
  varid: string;
};

export type TLoginSettings = {
    userid: number;
    email: string;
    fname: string;
    lname: string;
    password: string;
    created: Date;
    temp_password: number;
    isactive: 0 | 1;
    type: TRole;
    userTier: TTier;
    capsuleUserName: string | null;
    billingType: string | null;
    defaultC2C: string;
    defaultExtend: boolean;
    C2Con: 0 | 1;
    localPresenceLite: 0 | 1;
    localPresence: 0 | 1;
    localPresenceOn: 0 | 1;
    localPresenceProgressive: 0 | 1;
    FSvmID: string | number | null;
    userEXT: number;
    FSCallFlowID: string | number | null;
    countrycode: string;
    preferredServer: string;
    callerid: string;
    calleridName: string;
    PlivoUser: 0 | 1;
    FSuserID: string | number | null;
    userscol: string;
    pop: 0 | 1;
    dispose: 0 | 0;
    playEndSound: 0 | 1;
    canBeManaged: 0 | 1;
    mediaMetricsOn: 0 | 1;
    recordCalls: 0 | 1;
    inPowerlistSession: 0 | 1;
    playStartSound: 0 | 1;
    newUser: 0 | 1 | boolean;
    webrtcRing: string | number | null;
    webrtcAudio: string | number | null;
    othersCanViewRecordings: 0 | 1;
    overrideDnc: 0 | 1;
    switchUser: 0 | 1;
    setSuperadminPermissions: 0 | 1;
    inactiveDate: string | number | null;
    webrtcMic: string | number | null;
    PowerListEarliestCall: number;
    PowerListLatestCall: number;
    webrtcMicLabel: string | null;
    webrtcRingLabel: string | null;
    webrtcAudioLabel: string | null;
    vmDropDetectionType: string;
    ptype: number;
    pass2: string;
    sendRecordingtoCRM: 0 | 1;
    allowMultipleIncomingCalls: 0 | 1;
    canCallCoach: 0 | 1;
    smsDID: string;
    systemTeamId?: number | null;
    useCellService: 0 | 1;
    showDispostion: 0 | 1;
    DIDGreeting: string;
    DIDRingAudio: string | number | null;
    DIDNoActionAnswer: string | number | null;
    LightModeon: 0 | 1;
    Disposeon: 0 | 1;
    styleMode: string | number | null;
    compactMode: string | number | null;
    DND: 0 | 1;
    audioDeviceGain: 0 | 1;
    audioDeviceEcho: 0 | 1;
    audioDeviceNoise: 0 | 1;
    orig_email: string | null;
    ftcBlocking: 0 | 1;
    useBusinessHoursEnabled: 0 | 1;
    userHours: string;
    closedAction: string;
    cellNumber: string | number | null;
    hasCell: 0 | 1;
    cellCC: string | number | null;
    canCallCoach2: 0 | 1;
    devices: TDevice[];
    defaultDevice: TDefaultDevice;
    quietMode: boolean;
    preventHeldCallDispose?: 0 | 1;
    directoryConference?: any;
    crms?: CRMs;
    queues?: Queue[];
};

const initialState: Partial<TLoginSettings> = {};

export const loginSlice = createSlice({
  name: 'login',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    loginPatch: (
      state,
      action: PayloadAction<Partial<TLoginSettings>>,
    ): Partial<TLoginSettings> => ({ ...state, ...action.payload }),
    loginPut: (
      _state,
      action: PayloadAction<Partial<TLoginSettings>>,
    ): Partial<TLoginSettings> => action.payload,
  },
});

export const { loginPut, loginPatch } = loginSlice.actions;

// The function below is called a selector and allows us to select a value from
// the state. Selectors can also be defined inline where they're used instead of
// in the slice file. For example: `useSelector((state: RootState) => state.plivo.value)`
export const selectLoginSettings = (state: RootState) => state.login;

export const loginReducer = loginSlice.reducer;
